import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { essentialsApi, type CountryEssentials, type AdvisoryLevel, type TapWater } from '../../../api/essentials.api';
import { CountryEssentialsSkeleton } from './InfoSkeletons';
import InfoSectionHeader from './InfoSectionHeader';
import {
  Icon, Banknote, Globe, Phone, Plug, Car, Droplets, Landmark,
  ShieldCheck, ShieldAlert, AlertTriangle, ExternalLink, Clock, type LucideIcon,
} from '../../../components/ui/Icon';

const ADVISORY_STYLE: Record<AdvisoryLevel, { box: string; chip: string }> = {
  normal:          { box: 'bg-green-50/70 border-green-100 text-green-800 dark:bg-green-900/15 dark:border-green-900/50 dark:text-green-300', chip: 'bg-green-100 dark:bg-green-900/40' },
  see_advisory:    { box: 'bg-sky-50/70 border-sky-100 text-sky-800 dark:bg-sky-900/15 dark:border-sky-900/50 dark:text-sky-300',             chip: 'bg-sky-100 dark:bg-sky-900/40' },
  avoid_essential: { box: 'bg-amber-50/70 border-amber-100 text-amber-800 dark:bg-amber-900/15 dark:border-amber-900/50 dark:text-amber-300', chip: 'bg-amber-100 dark:bg-amber-900/40' },
  avoid_all:       { box: 'bg-red-50/70 border-red-100 text-red-800 dark:bg-red-900/15 dark:border-red-900/50 dark:text-red-300',             chip: 'bg-red-100 dark:bg-red-900/40' },
};

const WATER_TONE: Record<TapWater, { dot: string; text: string }> = {
  safe:    { dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400' },
  caution: { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400' },
  unsafe:  { dot: 'bg-red-500',   text: 'text-red-700 dark:text-red-400' },
};

function Tile({ icon, label, accent = false, children }: {
  icon: LucideIcon; label: string; accent?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="card p-4 flex flex-col gap-4 min-h-[118px]">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] tracking-label uppercase text-slate-400 dark:text-slate-500 truncate">{label}</span>
        <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${accent
          ? 'bg-coral-50 text-coral-500 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-canvas text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
          <Icon as={icon} size={14} />
        </span>
      </div>
      <div className="mt-auto font-serif text-xl leading-tight text-slate-900 dark:text-slate-100 min-w-0">{children}</div>
    </div>
  );
}

const SUB = 'block mt-1 font-sans text-xs text-slate-400 dark:text-slate-500 truncate';

export default function TripEssentialsPanel({ tripId }: { tripId: string }) {
  const { i18n } = useTranslation();
  const [countries, setCountries] = useState<CountryEssentials[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    essentialsApi.get(tripId, i18n.language)
      // ADR-0030: one section per country the trip visits.
      .then(r => { if (alive) setCountries(r.countries.map(c => c.essentials)); })
      .catch(() => { /* non-blocking: panel simply hides */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tripId, i18n.language]);

  if (loading) return <CountryEssentialsSkeleton />;
  return <>{countries.map(c => <CountrySection key={c.countryCode} data={c} />)}</>;
}

function CountrySection({ data }: { data: CountryEssentials }) {
  const { t } = useTranslation();
  const p = data.practical;
  const emergencyNumbers = p
    ? (p.emergency.general
        ? [{ label: t('essentials.emergencyGeneral'), num: p.emergency.general }]
        : ([
            p.emergency.police    && { label: t('essentials.emergencyPolice'),    num: p.emergency.police },
            p.emergency.ambulance && { label: t('essentials.emergencyAmbulance'), num: p.emergency.ambulance },
            p.emergency.fire      && { label: t('essentials.emergencyFire'),      num: p.emergency.fire },
          ].filter(Boolean) as { label: string; num: string }[]))
    : [];

  const advisory = data.advisory ? ADVISORY_STYLE[data.advisory.level] : null;

  return (
    <section className="mb-10">
      <InfoSectionHeader
        icon={data.flag ?? '🌍'}
        title={t('essentials.heading', { country: data.countryName ?? data.countryCode })}
      />

      {data.advisory && advisory && (
        <a
          href={data.advisory.url} target="_blank" rel="noopener noreferrer"
          className={`group flex items-center gap-3 pl-2 pr-4 py-2 rounded-2xl border mb-4 text-sm transition-colors ${advisory.box}`}
        >
          <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${advisory.chip}`}>
            <Icon as={data.advisory.level === 'normal' ? ShieldCheck : ShieldAlert} size={17} />
          </span>
          <span className="flex-1 font-semibold">{t(`essentials.advisory.${data.advisory.level}`)}</span>
          <Icon as={ExternalLink} size={14} className="opacity-50 group-hover:opacity-90 transition-opacity" />
        </a>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {data.currency && (
          <Tile icon={Banknote} label={t('essentials.currency')}>
            {data.currency.code}{data.currency.symbol ? <span className="text-slate-400 dark:text-slate-500"> · {data.currency.symbol}</span> : null}
            <span className={SUB}>{data.currency.name}</span>
          </Tile>
        )}
        {data.languages.length > 0 && (
          <Tile icon={Globe} label={t('essentials.language')}>
            {data.languages.slice(0, 2).join(', ')}
          </Tile>
        )}
        {data.capital && (
          <Tile icon={Landmark} label={t('essentials.capital')}>
            {data.capital}
          </Tile>
        )}
        {data.callingCode && (
          <Tile icon={Phone} label={t('essentials.callingCode')}>
            {data.callingCode}
          </Tile>
        )}
        {p && (
          <Tile icon={Plug} label={t('essentials.plug')}>
            {t('essentials.plugType')} {p.plugs.join('/')}
            <span className={SUB}>{p.voltage}V · {p.frequency}Hz</span>
          </Tile>
        )}
        {p && (
          <Tile icon={Car} label={t('essentials.driving')}>
            {t(`essentials.drive.${p.driving}`)}
          </Tile>
        )}
        {p && (
          <Tile icon={Droplets} label={t('essentials.tapWater')}>
            <span className={`flex items-center gap-2 ${WATER_TONE[p.tapWater].text}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${WATER_TONE[p.tapWater].dot}`} />
              {t(`essentials.water.${p.tapWater}`)}
            </span>
          </Tile>
        )}
        {emergencyNumbers.length > 0 && (
          <Tile icon={AlertTriangle} label={t('essentials.emergency')} accent>
            {emergencyNumbers.map(e => (
              <span key={e.label} className="flex items-baseline gap-1.5">
                <a href={`tel:${e.num}`} className="text-coral-700 dark:text-red-400 hover:underline">{e.num}</a>
                <span className="font-sans text-xs text-slate-400 dark:text-slate-500">{e.label}</span>
              </span>
            ))}
          </Tile>
        )}
      </div>

      <p className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-400 dark:text-slate-500">
        <Icon as={Clock} size={11} />
        {t('essentials.practicalDisclaimer')}
      </p>
    </section>
  );
}
